import { QueryClient } from "@tanstack/react-query";

// Query key factories for consistent cache management
export const queryKeys = {
  /**
   * Hydrate localStorage from cookie session once per load (deduped across all useAuth() callers).
   */
  auth: {
    bootstrapSession: ["auth", "bootstrapSession"] as const,
  },
  listings: {
    all: ["listings"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.listings.all, "list", filters] as const,
    browse: (params?: object) =>
      [...queryKeys.listings.all, "browse", params ?? {}] as const,
    priceBounds: ["listings", "browse", "priceBounds"] as const,
    detail: (id: string) => [...queryKeys.listings.all, "detail", id] as const,
    availability: (id: string) => [...queryKeys.listings.all, "availability", id] as const,
    /** get_listing_availability_for_client — keyed by listing + date window */
    availabilityRange: (id: string, start: string, end: string) =>
      [...queryKeys.listings.all, "availability", id, start, end] as const,
  },
  bookings: {
    all: ["bookings"] as const,
    list: (userId: string) => [...queryKeys.bookings.all, "list", userId] as const,
    detail: (id: string) => [...queryKeys.bookings.all, "detail", id] as const,
    /** Booking details page: user + booking + reviews (see useBookingDetails) */
    detailBundle: (id: string) => [...queryKeys.bookings.all, "detailBundle", id] as const,
  },
  reviews: {
    all: ["reviews"] as const,
    /** supabase.rpc(get_user_reviews) — received + given + owner_rating (profile / dashboard) */
    profileBundle: (userId: string) => [...queryKeys.reviews.all, "profileBundle", userId] as const,
    byUser: (userId: string) => [...queryKeys.reviews.all, "byUser", userId] as const,
    byListing: (listingId: string) => [...queryKeys.reviews.all, "byListing", listingId] as const,
    summary: (userId: string) => [...queryKeys.reviews.all, "summary", userId] as const,
    bookingEligibility: (bookingId: string) =>
      [...queryKeys.reviews.all, "bookingEligibility", bookingId] as const,
  },
  profile: {
    all: ["profile"] as const,
    detail: (userId: string) => [...queryKeys.profile.all, "detail", userId] as const,
    /** Current user profile bundle (API + Stripe status) — key includes user id when known */
    me: (userId: string) => [...queryKeys.profile.all, "me", userId] as const,
    /** supabase.rpc(get_my_profile_header) — id, full_name, avatar_url for nav avatar */
    header: (userId: string) => [...queryKeys.profile.all, "header", userId] as const,
  },
  favorites: {
    all: ["favorites"] as const,
    list: (userId: string) => [...queryKeys.favorites.all, "list", userId] as const,
  },
  messages: {
    conversations: (userId: string) => ["messages", "conversations", userId] as const,
  },
  settings: {
    all: ["settings"] as const,
    bundle: (userId: string) => [...queryKeys.settings.all, "bundle", userId] as const,
  },
  payments: {
    all: ["payments"] as const,
    stripeAccount: (userId: string) =>
      [...queryKeys.payments.all, "stripeAccount", userId] as const,
  },
  /** User dashboard — supabase.rpc(get_user_dashboard_*) bundle; key by user id */
  dashboard: {
    all: ["dashboard"] as const,
    main: (userId: string) => [...queryKeys.dashboard.all, "main", userId] as const,
    /** Sharer listings + earnings (/api/dashboard/listings) */
    listings: (userId: string) => [...queryKeys.dashboard.all, "listings", userId] as const,
  },
  /** supabase.rpc(get_sidebar_counts) */
  sidebar: {
    counts: (userId: string) => ["sidebar", "counts", userId] as const,
  },
  home: {
    bundle: (userId: string) => ["home", "bundle", userId] as const,
    /** supabase.rpc(get_top_listings_for_home) */
    topListings: (limit: number, userId?: string | null) =>
      ["home", "topListings", limit, userId ?? null] as const,
  },
  /** Stripe: self via get_my_stripe_connect_status RPC (useStripeAccountStatus); admin other user via get_user_stripe_status RPC */
  stripeConnect: {
    legacyStatus: ["stripeConnect", "legacy"] as const,
    adminUser: (userId: string) => ["stripeConnect", "admin", userId] as const,
  },
  search: {
    suggestions: (q: string) => ["search", "suggestions", q] as const,
  },
  /**
   * Preferences: supabase.rpc(get_notification_preferences).
   * Unread badge: supabase.rpc(get_notifications_unread_meta).
   */
  notifications: {
    all: ["notifications"] as const,
    preferences: (userId: string) => ["notifications", "preferences", userId] as const,
    unreadCount: (userId: string) => ["notifications", "unreadCount", userId] as const,
  },
  categories: {
    all: ["categories"] as const,
    list: (active?: boolean) => [...queryKeys.categories.all, "list", active] as const,
    active: ["categories", "active"] as const,
    /** supabase.rpc(get_category_listing_counts) */
    counts: ["categories", "counts"] as const,
    adminList: (params?: Record<string, unknown>) =>
      [...queryKeys.categories.all, "admin", params ?? {}] as const,
  },
  locations: {
    all: ["locations"] as const,
    states: () => [...queryKeys.locations.all, "states"] as const,
    cities: (stateId: string) => [...queryKeys.locations.all, "cities", stateId] as const,
  },
  /** Admin CMS static pages — GET /api/admin/cms/pages/query */
  cmsPages: {
    all: ["cmsPages"] as const,
    list: (params: { page: number; limit: number; search: string; status: string }) =>
      [...queryKeys.cmsPages.all, "list", params] as const,
    detail: (id: string) => [...queryKeys.cmsPages.all, "detail", id] as const,
  },
  /** Admin Visual Component CMS — GET /api/admin/visual-cms/pages/query */
  visualCmsPages: {
    all: ["visualCmsPages"] as const,
    list: (params: {
      page: number;
      limit: number;
      search: string;
      status: string;
      page_type: string;
    }) => [...queryKeys.visualCmsPages.all, "list", params] as const,
    detail: (id: string) => [...queryKeys.visualCmsPages.all, "detail", id] as const,
    versions: (pageId: string) => [...queryKeys.visualCmsPages.all, "versions", pageId] as const,
  },
  /** Admin panel — invalidate `admin.all` after mutations that touch admin APIs */
  admin: {
    all: ["admin"] as const,
    /** Prefix — invalidate after mutations affecting any dashboard RPC query */
    dashboardPrefix: ["admin", "dashboard"] as const,
    /** supabase.rpc(get_admin_dashboard) — bundle + KPIs; key includes KPI date range */
    dashboard: (range: { from: string; to: string }) =>
      [...queryKeys.admin.dashboardPrefix, range.from, range.to] as const,
    /** GET /api/admin/analytics — SQL aggregation bundle */
    analytics: (params: { range: string; from: string; to: string }) =>
      ["admin", "analytics", params] as const,
    /** Admin notifications: supabase.rpc(get_admin_notifications_page); legacy GET kept */
    notifications: {
      all: ["admin", "notifications"] as const,
      list: (filters: { type: string | null; read: "all" | "unread" | "read" }) =>
        ["admin", "notifications", "list", filters] as const,
      /** Lightweight unread total for header badge (shares cache with first list page when possible) */
      unreadMeta: ["admin", "notifications", "unreadMeta"] as const,
    },
    roleCheck: (userId: string) => ["admin", "role", userId] as const,
    /** Admin locations — supabase.rpc get_admin_states (paginated + search) */
    statesList: (params: {
      tab: "active" | "inactive";
      page: number;
      limit: number;
      search: string;
    }) =>
      ["admin", "states", "list", params.tab, params.page, params.limit, params.search] as const,
    /** Admin locations — supabase.rpc get_admin_cities (paginated + search) */
    citiesList: (params: {
      tab: "active" | "inactive";
      page: number;
      limit: number;
      search: string;
    }) =>
      ["admin", "cities", "list", params.tab, params.page, params.limit, params.search] as const,
    /** Active AU states for city dropdown — get_admin_active_state_options */
    statesDropdown: ["admin", "states", "dropdown", "active"] as const,
    /** Admin listings page — use `listings.all` to invalidate every list + stats */
    /** Prefix: invalidate any paginated admin users query */
    usersListPrefix: ["admin", "users", "list"] as const,
    /** Admin /admin/users — supabase.rpc(get_admin_users_with_stats); key includes filters + page */
    usersList: (params: Record<string, unknown>) =>
      [...queryKeys.admin.usersListPrefix, params] as const,
    /** Admin user detail + tab RPCs (get_admin_user_details, get_user_* ) */
    userDetail: (userId: string) => ["admin", "users", "detail", userId] as const,
    userDetailPrefix: (userId: string) => ["admin", "users", "detail", userId] as const,
    userDetailListings: (userId: string) => ["admin", "users", "detail", userId, "listings"] as const,
    userDetailBookings: (userId: string) => ["admin", "users", "detail", userId, "bookings"] as const,
    userDetailReviews: (userId: string) => ["admin", "users", "detail", userId, "reviews"] as const,
    userDetailReports: (userId: string) => ["admin", "users", "detail", userId, "reports"] as const,
    /** supabase.rpc(get_admin_bookings_page) — paginated list + stats */
    bookingsList: (params: {
      page: number;
      limit: number;
      sortBy: string;
      order: string;
      search: string;
      status: string;
    }) => ["admin", "bookings", "list", params] as const,
    /** Invalidate all admin booking queries (list + detail) */
    bookingsAll: ["admin", "bookings"] as const,
    /** supabase.rpc(get_booking_detail_admin) */
    bookingDetail: (bookingId: string) => ["admin", "bookings", "detail", bookingId] as const,
    /** Admin review dashboard — get_review_analytics + related RPCs */
    reviewAnalytics: (fromIso: string, toIso: string) =>
      ["admin", "reviews", "analytics", fromIso, toIso] as const,
    bookingHistory: (bookingId: string) => ["admin", "bookings", "history", bookingId] as const,
    bookingMessages: (bookingId: string) => ["admin", "bookings", "messages", bookingId] as const,
    /** supabase.rpc(get_admin_conversations) */
    messagesConversations: (filters: Record<string, unknown>) =>
      [...queryKeys.admin.all, "messages", "conversations", filters] as const,
    /** supabase.rpc(get_admin_conversation_detail) — include page for message pagination */
    messagesConversationDetail: (conversationId: string, page: number) =>
      [...queryKeys.admin.all, "messages", "conversation", conversationId, page] as const,
    listings: {
      all: ["admin", "listings"] as const,
      list: (
        params: {
          page: number;
          tab: "user" | "my";
          search: string;
          category: string;
          status: string;
          sortBy: string;
          deletedFilter: string;
        },
        currentUserId: string
      ) => ["admin", "listings", "list", currentUserId, params] as const,
      stats: (tab: string, userId: string) => ["admin", "listings", "stats", tab, userId] as const,
      /** Admin listing detail — get_admin_listing_detail_bundle */
      detail: (listingId: string) => ["admin", "listings", "detail", listingId] as const,
    },
  },
  /** Profile access row for gating UI (see useUserAccess) */
  userAccess: {
    state: (userId: string) => ["userAccess", userId] as const,
  },
};

// Create the query client with web-optimized defaults
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - consider data fresh for this time
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for this time
      retry: (failureCount, error: any) => {
        // Don&apos;t retry on 404s or other client errors
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry up to 3 times for server errors or network issues
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      refetchOnWindowFocus: false, // Don&apos;t refetch on window focus for better UX
    },
    mutations: {
      retry: 1, // Retry mutations once on failure
    },
  },
});
